/* eslint-disable no-nested-ternary */
import React, { useContext, Fragment } from 'react';
import { NavBar, Tabs, List, Popup, Ellipsis, Badge, Switch, Divider } from 'antd-mobile';
import { CloseOutline, ExclamationCircleOutline } from 'antd-mobile-icons';
import moment from 'moment';
import get from 'lodash/get';
import styled from 'styled-components';
import Signatures from './Signatures';
import { updateDefectTask, updateWorkOrderOperationTask } from '../api';
import InputComponent from './Input';
import Files from '../containers/Files';
import Notes from '../containers/Notes';
import { featureEnabled, matchesPermission } from '../utils/permission-helpers';
import UserSelfContext from '../utils/self-context';
import { OBJECT_TYPE_SUBTASK } from '../utils/constants';
import CustomFieldInputItem from './CustomFieldsInput';

const TaskWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;
const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
	width: 100%;
	background: #f0f2f5;
`;

const Overview = styled.div`
	padding: 12px 0px;
`;

const TaskInfo = styled.div`
	padding: 0px 12px;
	background: #fff;

	.adm-list-item-title {
		margin-bottom: 8px;
		display: flex;
		align-items: center;
	}

	h2 {
		margin: 0px 8px 0px 0px;
		display: inline-block;
		color: var(--adm-color-text);
	}

	.adm-ellipsis {
		a {
			margin-left: 8px;
		}
	}
`;

const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const Description = styled(List.Item)`
	font-size: var(--adm-font-size-9);
`;

const TabBar = styled(Tabs)`
	display: flex;
	flex-direction: column;
	height: 100%;
	.adm-tabs-header {
		background: #fff;
	}

	.adm-tabs-content {
		flex: 1;
		overflow: auto;
		padding: 0;
	}
`;

function SubTaskPopup(props) {
	const {
		subTask,
		visible,
		onClose,
		onChange
	} = props;
	const { userSelf } = useContext(UserSelfContext);

	async function updateSubTask(data) {
		const payload = data;
		payload.id = subTask.id;

		try {
			if (subTask.work_order_op_id) {
				await updateWorkOrderOperationTask(subTask.work_order_op_id, data);
			}

			if (subTask.defect_id) {
				await updateDefectTask(subTask.defect_id, data);
			}

			onChange(subTask.id);
		} catch (e) {
			// ignore
		}
	}

	async function onNotApplicableChange(value) {
		await updateSubTask({
			id: subTask.id,
			not_applicable: !value
		});
	}

	return (
		<Popup
			position='right'
			visible={visible}
			bodyStyle={{ width: '100%' }}
			onClose={onClose}
		>
			<TaskWrapper>
				<Nav backArrow={<CloseOutline />} onBack={onClose} back={''}>
					Subtask
				</Nav>
				<TaskInfo>
					<Description><Ellipsis
						direction='end'
						rows={4} content={subTask.description || '-'}
						expandText="Show"
						collapseText="Hide"
					/></Description>
				</TaskInfo>
				<ContentWrapper>
					<TabBar defaultActiveKey="overview">
						<Tabs.Tab
							title={'Overview'}
							key='overview'
						>
							<Overview>
								<Fragment>
									<InputComponent
										placeholder="ATA"
										onSave={updateSubTask}
										dataKey="ata"
										requiredRulesObjectType={OBJECT_TYPE_SUBTASK}
										disabled={!matchesPermission(userSelf, 'edit_work_order_operations') || subTask.operation_id}
										initialValue={subTask.ata}
										title="ATA"
									/>
									<InputComponent
										placeholder="Category"
										onSave={updateSubTask}
										dataKey="category"
										requiredRulesObjectType={OBJECT_TYPE_SUBTASK}
										disabled={!matchesPermission(userSelf, 'edit_work_order_operations') || subTask.operation_id}
										initialValue={[subTask.category]}
										title="Category"
										type="select"
										options={[
											...(get(userSelf, 'settings.maintenance_item_categories') || []).map(t => ({ label: t, value: t }))
										]}
									/>
									<InputComponent
										placeholder="Critical task"
										onSave={updateSubTask}
										requiredRulesObjectType={OBJECT_TYPE_SUBTASK}
										dataKey="critical"
										type="switch"
										getCustomDisplayValue={val => (val ? <span style={{ color: '#ff411c', fontWeight: 'bold' }}><ExclamationCircleOutline /> Yes</span> : <span>No</span>)}
										disabled={!matchesPermission(userSelf, 'edit_work_order_operations')}
										initialValue={subTask.critical}
										title="Critical task"
									/>
									{(subTask.saved_custom_fields || [])
										.map(field => (
											<CustomFieldInputItem
												key={field.key}
												model={subTask}
												field={{ ...field, object_type: OBJECT_TYPE_SUBTASK }}
												onSave={updateSubTask}
												disabled={!matchesPermission(userSelf, 'edit_work_order_operations')}
											/>
										))}
									{subTask.is_optional && <List header={'Applicable'}>
										<List.Item
										>
											<Switch checked={!subTask.not_applicable} onChange={onNotApplicableChange} />
										</List.Item>
									</List>}
									{subTask.is_input && <InputComponent
										placeholder="Insert input value"
										onSave={updateSubTask}
										isRequired={true}
										dataKey="input_value"
										disabled={!matchesPermission(userSelf, 'edit_work_order_operations')}
										title={<strong>Input</strong>}
										initialValue={subTask.input_value}
									/>}
									{!subTask.not_applicable ? <InputComponent
										onSave={updateSubTask}
										disabled={!matchesPermission(userSelf, 'edit_work_order_operation')}
										dataKey="done_date"
										title="Done date"
										isRequired={true}
										type="date"
										initialValue={subTask.done_date}
									/> :
										<List header={'Done date'}>
											<List.Item>
												N/A
											</List.Item>
										</List>}
									{featureEnabled(userSelf, 'work_order_staff_enabled') ? (!subTask.not_applicable ? <Signatures
										onChange={(id, removed) => {
											if (!removed) {
												return updateSubTask({
													done_date: moment()
												});
											}

											return onChange(subTask.id);
										}}
										item={subTask}
										objectType={subTask.defect_id ? 'defect_task' : 'operation_task'}
									/> : <List header={'Signatures'}>
										<List.Item>
											N/A
										</List.Item>
									</List>) : null}
								</Fragment>
							</Overview>
						</Tabs.Tab>
						<Tabs.Tab
							title={<Badge
								content={subTask.files_count || null}
								style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
									Files
							</Badge>}
							key='files'
						>
							<Files
								onChange={() => onChange(subTask.id)}
								objectType="work_order_op_task"
								relatedObjectType="operation_task"
								inPopup={true}
								objectId={subTask.id}
								disabled={!matchesPermission(userSelf, 'manage_files_work_order_operations')}
								relatedObjectId={subTask.operation_task_id}
							/>
						</Tabs.Tab>
						<Tabs.Tab
							title={<Badge
								content={subTask.notes_count || null}
								style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
									Notes
							</Badge>}
							key='notes'
						>
							<Notes
								onChange={() => onChange(subTask.id)}
								objectType="work_order_op_task"
								relatedObjectType="operation_task"
								objectId={subTask.id}
								inPopup={true}
								disabled={!matchesPermission(userSelf, 'manage_files_work_order_operations')}
								relatedObjectId={subTask.operation_task_id}
							/>
						</Tabs.Tab>
					</TabBar>
				</ContentWrapper>
			</TaskWrapper>
		</Popup>
	);
}

export default SubTaskPopup;
